import { useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { PageTitle, Regular, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { useListMemberDependentsQuery } from "@/App/api/member";
import { DependentDropdown, MemberCardBack, MemberCardFront } from "@/App/components";
import { BlankPageWrapper } from "@/App/components/BlankPageWrapper";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useIsUnifiedUser, useNavigateClientNewTab } from "@/App/hooks";
import { getCustomerSupportNumber } from "@/App/utils/utils";

const TRANSLATION_PATH = "MEMBER_ID_CARD_PAGE";

export const MemberIDCard = () => {
  const { t: translate } = useTranslation();

  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);

  const [personCode, setPersonCode] = useState<string>(memberEligibility?.member?.person_code);

  const { isUnifiedUser } = useIsUnifiedUser();
  const navigate = useNavigateClientNewTab();

  const { data: dependents } = useListMemberDependentsQuery();
  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);
  const hasDependents = !isViewingDataholder && minorDependents.length > 0;

  const selectedPerson = personCode === memberEligibility?.member?.person_code ? memberEligibility?.member : minorDependents.find((dependent) => dependent?.member?.person_code === personCode)?.member;

  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);

  const onPrintCardPressed = () => {
    const newParams = new URLSearchParams();
    if (isViewingDataholder) {
      newParams.append(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
    } else if (hasDependents && personCode !== memberEligibility?.member?.person_code) {
      newParams.append(SearchParamKeys.PERSON_CODE, personCode);
    }
    navigate("/id_card", newParams);
  };


  return (
    <BlankPageWrapper>
      <Stack testId="member-card-container" spacing="large">
        <Stack spacing="small">
          <PageTitle>{translate(`${TRANSLATION_PATH}.title`)}</PageTitle>
          <Regular>{translate(`${TRANSLATION_PATH}.replacementCard`)} <a href={`tel: ${customerSupportNumber}`}>{customerSupportNumber}.</a></Regular>
        </Stack>
        <Stack>
          <DependentDropdown
            onChange={(value: string) => setPersonCode(value)}
            value={personCode}
            label={`${translate(`${TRANSLATION_PATH}.viewCardFor`)}:`}
            aria-label={`${translate(`${TRANSLATION_PATH}.viewCardFor`)}:`}
            dependents={minorDependents}
            member={memberEligibility.member}
          />
        </Stack>
        <Stack>
          <Stack spacing="large">
            <StackOrShelf alignment="center" spacing="large" isTabletNarrow>
              <MemberCardFront selectedPerson={selectedPerson} />
              {isUnifiedUser && (
                <MemberCardBack />
              )}
              </StackOrShelf>
            <Shelf alignment="center">
              <Button icon="print" testId="print-card" appearance="secondary" onClick={onPrintCardPressed}>
                {translate(`${TRANSLATION_PATH}.printCard`)}
              </Button>
            </Shelf>
          </Stack>
        </Stack>
        {
          isUnifiedUser && (
            <>
              <Stack spacing="small">
                <Subheading>{translate(`${TRANSLATION_PATH}.preCertification`)}</Subheading>
                <Regular>{translate(`${TRANSLATION_PATH}.callJudiHealth`)}<Semibold>1-833-599-1020</Semibold><br/>
                <Trans
                  i18nKey={`${TRANSLATION_PATH}.preCertificationMessage`}
                  components={{ br: <br /> }}
                /></Regular>
              </Stack>
              <Stack spacing="medium">
                <Stack spacing="small">
                  <Subheading>{translate(`${TRANSLATION_PATH}.memberClaims`)}</Subheading>
                  <Stack spacing="none">
                    <Regular>
                      <Semibold>Cigna Medical Claims</Semibold>
                    </Regular>
                    <Regular>PO Box 188061</Regular>
                    <Regular>Chattanooga, TN 37422 - 8061</Regular>
                    <br />
                    <Regular><Semibold>{translate(`${TRANSLATION_PATH}.electronicPayerId`)}:</Semibold> 62308</Regular>
                    <Regular>{translate(`${TRANSLATION_PATH}.cignaWebsiteMessage`)} <a href="https://www.mycigna.com">www.myCigna.com</a></Regular>
                    <br />
                    <Regular>*{translate(`${TRANSLATION_PATH}.cignaBenefitsDisclosure`)}</Regular>
                  </Stack>
                </Stack>
                <div><Tag type="system-high-contrast">Away from Home Care</Tag></div>
              </Stack>
              <Stack>
                <Stack spacing="small">
                  <Subheading>{translate(`${TRANSLATION_PATH}.rxClaims`)}</Subheading>
                  <Stack spacing="none">
                    <Regular>
                      <Semibold>Capital Rx</Semibold>
                    </Regular>
                    <Regular>Attn: Claims</Regular>
                    <Regular>9450 SW Gemini Dr. #87234</Regular>
                    <Regular>Beaverton, Oregon 97008-7105</Regular>
                    <br />
                    <Semibold>{translate(`${TRANSLATION_PATH}.memberPharmacyHelpLine`)}:</Semibold>
                    <Regular>1-833-599-1020</Regular>
                  </Stack>
                </Stack>
              </Stack>
            </>
          )
        }
      </Stack>
    </BlankPageWrapper>
  );
};
